import { useLanguage } from "@/i18n/LanguageContext";
import { Clock, CalendarDays, Check } from "lucide-react";

const CARD_ICONS = [Clock, CalendarDays];

function PriceCard({ card, idx, popular, highlighted }) {
  const Icon = CARD_ICONS[idx];
  const mutedText = highlighted ? "text-red-100" : "text-gray-400";

  return (
    <div
      data-testid={`pricing-card-${idx}`}
      className={`relative p-8 lg:p-10 border ${
        highlighted ? "bg-red-600 border-red-600" : "bg-gray-800 border-gray-700"
      }`}
    >
      {highlighted && (
        <span
          data-testid="pricing-popular-badge"
          className="absolute -top-3 left-8 bg-white text-gray-900 text-xs font-bold uppercase tracking-wider px-3 py-1 font-body"
        >
          {popular}
        </span>
      )}
      <div
        className={`w-12 h-12 flex items-center justify-center mb-6 ${
          highlighted ? "bg-white" : "bg-red-600"
        }`}
      >
        <Icon
          className={`w-6 h-6 ${highlighted ? "text-red-600" : "text-white"}`}
          strokeWidth={2}
        />
      </div>
      <p
        className={`text-xs font-bold uppercase tracking-[0.2em] font-body mb-2 ${
          highlighted ? "text-red-100" : "text-red-500"
        }`}
      >
        {card.label}
      </p>
      <h3 className="text-xl font-bold text-white font-heading mb-4">
        {card.duration}
      </h3>
      <div className="flex items-baseline gap-2 mb-4">
        {card.prefix && (
          <span className={`text-sm font-body ${mutedText}`}>{card.prefix}</span>
        )}
        <span
          data-testid={`pricing-price-${idx}`}
          className="text-5xl font-black text-white font-heading tracking-tight"
        >
          {card.price}
        </span>
        {card.unit && (
          <span className={`text-lg font-body ${mutedText}`}>{card.unit}</span>
        )}
      </div>
      <p className={`text-sm leading-relaxed font-body ${mutedText}`}>
        {card.description}
      </p>
    </div>
  );
}

function DiscountGrid({ discounts }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-gray-700 border border-gray-700">
      {discounts.map((d, idx) => (
        <div
          key={d.days}
          data-testid={`pricing-discount-${idx}`}
          className="bg-gray-800 p-5 text-center hover:bg-gray-750 transition-colors duration-200"
        >
          <p className="text-2xl font-black text-red-500 font-heading">-{d.off}</p>
          <p className="text-xs uppercase tracking-wider text-gray-400 font-body mt-1">
            {d.days}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Pricing() {
  const { t } = useLanguage();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 lg:py-32 bg-gray-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="inline-block mb-6">
            <span
              data-testid="pricing-tag"
              className="text-xs font-bold tracking-[0.2em] uppercase text-red-500 font-body"
            >
              {t.pricing.tag}
            </span>
            <div className="w-full h-0.5 bg-red-500 mt-1" />
          </div>
          <h2
            data-testid="pricing-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-none text-white font-heading"
          >
            {t.pricing.title}
          </h2>
          <p className="mt-6 text-base text-gray-400 font-body leading-relaxed">
            {t.pricing.subtitle}
          </p>
        </div>

        {/* Price cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
          {t.pricing.cards.map((card, idx) => (
            <PriceCard
              key={card.label}
              card={card}
              idx={idx}
              popular={t.pricing.popular}
              highlighted={idx === 1}
            />
          ))}
        </div>

        {/* Discounts */}
        <div className="mt-16 max-w-4xl">
          <h3
            data-testid="pricing-discount-title"
            className="text-lg font-bold text-white font-heading mb-6 flex items-center gap-3"
          >
            <span className="w-8 h-8 bg-red-600 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
            </span>
            {t.pricing.discountTitle}
          </h3>
          <DiscountGrid discounts={t.pricing.discounts} />
          <p
            data-testid="pricing-note"
            className="mt-6 text-sm text-gray-500 font-body"
          >
            {t.pricing.note}
          </p>
        </div>
      </div>
    </section>
  );
}
